import {
  ReachCampaignDetails,
  SourcingPurchaseDetails,
} from 'api-clients/monolith';
import _round from 'lodash/round';
import { useCallback, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { HiringTarget } from '@/components/pages/edit-campaign/use-hiring-target';

import { CampaignParameters } from './types';

export interface ReachSummary {
  budget?: number;
  expectedCpa?: number;
  expectedConversion?: number;
  targetApplicants?: number;
  targetHires?: number;
  perDayBudget?: number;
}

interface UseReachSummaryProps {
  hiringTarget: HiringTarget;
  campaign?: SourcingPurchaseDetails;
  selectedPublisher?: ReachCampaignDetails;
  hookForm: UseFormReturn<CampaignParameters>;
}

export function useReachSummary({
  hiringTarget,
  campaign,
  selectedPublisher,
  hookForm,
}: UseReachSummaryProps) {
  const campaignExpectedCpa = campaign?.expected_cpa ?? 0;
  const campaignExpectedConversion = campaign?.expected_conversion ?? 0;
  const noOfCampaigns = campaign?.reach_campaigns.length ?? 0;

  const selectedPublisherExpectedCpa = selectedPublisher?.expected_cpa ?? 0;
  const selectedPublisherExpectedConversion =
    selectedPublisher?.expected_conversion ?? 0;

  const {
    targetBudget,
    initialTargetBudget,
    remainingDurationBudget,
    overlapDuration,
  } = hiringTarget;

  const {
    watch,
    formState: { dirtyFields },
  } = hookForm;
  const inputBudget = watch('budget') || 0;
  const inputExpectedCpa = watch('expected_cpa') || 0;
  const inputExpectedConversion = watch('expected_conversion') || 0;

  /**
   * Calculates the remaining duration of a publisher's campaign.
   *
   * @param publisher - The publisher's campaign details.
   * @returns The remaining duration in days.
   */
  const getRemainingDuration = useCallback(
    (publisher: ReachCampaignDetails) => {
      const expectedDuration = publisher?.duration_stats?.expected ?? 0;
      const actualDuration = publisher?.duration_stats?.actual ?? 0;

      if (
        publisher.status === 'completed' ||
        expectedDuration - actualDuration <= 0
      ) {
        return 0;
      }

      // If the publisher is the selected publisher and the end date is dirty, calculate the duration from the dates,
      // otherwise use the duration calculated in the backend. To avoid duration mismatch between the frontend and the backend.
      if (
        selectedPublisher?.reach_campaign_id === publisher.reach_campaign_id &&
        dirtyFields.end_at
      ) {
        return overlapDuration(
          publisher.starts_at,
          publisher.end_at ?? undefined,
        );
      }

      return expectedDuration - actualDuration;
    },
    [overlapDuration, dirtyFields.end_at, selectedPublisher],
  );

  /**
   * Calculates the campaign budget based on the remaining duration budget and the budget spent.
   *
   * Formula:
   * newBudget = total budget of all publishers - initial target budget of selected publisher + target budget of selected publisher
   * total budget of all publishers = sum of remaining duration budget for all publishers + budget spent
   *
   * When the form is pristine, `initialTargetBudget` may still be 0 on the first
   * paint (it is stored in a ref updated in useEffect). Fall back to `targetBudget`
   * so we do not double-count spent + projected (e.g. 2 + 17.62 + 20 → 39).
   */
  const budget = useMemo(() => {
    const currentBudget =
      campaign?.reach_campaigns.reduce((acc, publisher) => {
        return (
          acc +
          remainingDurationBudget(
            'daily',
            getRemainingDuration(publisher),
            publisher.budget,
          )
        );
      }, campaign?.budget_spent ?? 0) || 0;

    const baselineTargetBudget = initialTargetBudget || targetBudget;

    return Math.round(currentBudget - baselineTargetBudget + targetBudget) || 0;
  }, [
    campaign,
    initialTargetBudget,
    targetBudget,
    remainingDurationBudget,
    getRemainingDuration,
  ]);

  /**
   * Recalculates the average expected CPA when one publisher's expected CPA is updated.
   *
   * Formula:
   * newExpectedCpa = (currentAvg * totalCampaigns - oldValue + newValue) / totalCampaigns
   */
  const expectedCpa = useMemo(() => {
    if (noOfCampaigns === 0) return 0;

    return (
      _round(
        (campaignExpectedCpa * noOfCampaigns -
          selectedPublisherExpectedCpa +
          inputExpectedCpa) /
          noOfCampaigns,
        2,
      ) || 0
    );
  }, [
    campaignExpectedCpa,
    noOfCampaigns,
    selectedPublisherExpectedCpa,
    inputExpectedCpa,
  ]);

  /**
   * Recalculates the average expected conversion when one publisher's expected conversion is updated.
   *
   * Formula:
   * newExpectedConversion = (currentAvg * totalCampaigns - oldValue + newValue) / totalCampaigns
   */
  const expectedConversion = useMemo(() => {
    if (noOfCampaigns === 0) return 0;

    return (
      _round(
        (campaignExpectedConversion * noOfCampaigns -
          selectedPublisherExpectedConversion +
          inputExpectedConversion) /
          noOfCampaigns,
        2,
      ) || 0
    );
  }, [
    campaignExpectedConversion,
    noOfCampaigns,
    selectedPublisherExpectedConversion,
    inputExpectedConversion,
  ]);

  const targetApplicants = useMemo(() => {
    if (expectedCpa === 0) return 0;

    return Math.round(budget / expectedCpa) || 0;
  }, [budget, expectedCpa]);

  const targetHires = useMemo(() => {
    if (expectedCpa === 0) return 0;

    return Math.round(((budget / expectedCpa) * expectedConversion) / 100) || 0;
  }, [budget, expectedCpa, expectedConversion]);

  /**
   * Recalculates the per-day budget when one publisher's budget is updated.
   *
   * Formula:
   * newPerDayBudget = (currentPerDayBudget - oldValue + newValue) / totalCampaigns
   */
  const perDayBudget = useMemo(() => {
    const currentPerDayBudget =
      campaign?.reach_campaigns
        .filter(publisher => publisher.status !== 'completed')
        .reduce((acc, publisher) => {
          return acc + publisher.budget;
        }, 0) || 0;

    return _round(
      currentPerDayBudget - (selectedPublisher?.budget ?? 0) + inputBudget,
      2,
    );
  }, [campaign?.reach_campaigns, inputBudget, selectedPublisher?.budget]);

  return {
    budget,
    expectedCpa,
    expectedConversion,
    targetApplicants,
    targetHires,
    perDayBudget,
  };
}
